import { type QueryClient } from '@tanstack/react-query';
import { AGENT_MEMORIES_QUERY_KEY, WORKFLOW_KEYS } from '../../contracts/keys';

import { GET_SESSION_CLINICAL_CODES } from '@heidi/coding/contracts';
import { GET_PATIENT_DOCUMENTS, GET_PATIENT_PROFILES } from '@heidi/patients/contracts';
import {
  GET_DOCUMENTS_BY_ID,
  GET_SESSION_BY_ID,
  GET_SESSION_CONTEXT_FILES,
} from '@heidi/session/contracts';
import { GET_CLINICAL_TASKS_BY_SESSION_ID } from '@heidi/tasks/contracts';

import type { Artifact } from './artifacts/state';

/**
 * Invalidate the react-query cache backing an artifact so an
 * agent-driven change (note rewrite, patient context update, task edit)
 * is reflected the moment the canvas opens or re-opens.
 *
 * The session's *document list* (`GET_DOCUMENTS_BY_ID`) is what `ScribeTabs`
 * reads to build the per-document tabs, so it must be invalidated whenever a
 * tool writes a document into the session — otherwise a `create_session` that
 * cached an empty list (before the doc existed) keeps that stale list and the
 * brand-new `create_session_document` / generated-note tab never appears (it
 * deep-links to a `focusDocumentId` that isn't in the cached list). The doc's
 * own by-id query being cold is irrelevant: the tab only renders if the list
 * contains it.
 *
 * The list is invalidated with `refetchType: 'all'` because the observer is
 * often unmounted when the frame arrives (voice workspace overlay covering the
 * session view, or the user on another route). A default invalidation would
 * only mark it stale, so the tab strip would wait a full round-trip after the
 * session page mounts — refetching eagerly means the page renders the new tab
 * straight from a warm cache.
 */
/** The letters list has no contracts door, so the app passes its query factory. */
export type LettersQueryOptions = (sessionId: string) => {
  queryKey: readonly unknown[];
};

export const refreshArtifactData = (
  artifact: Artifact,
  queryClient: QueryClient,
  lettersQueryOptions: LettersQueryOptions
): void => {
  switch (artifact.type) {
    case 'session':
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId: artifact.sessionId }],
      });
      // The Context-tab chips (context documents) are a separate query
      // from the session record, so refresh them too — this is what makes
      // create / update / delete_session_context_document show up the
      // moment the agent opens the session on the Context tab.
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_CONTEXT_FILES, { sessionId: artifact.sessionId }],
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENTS_BY_ID, { sessionId: artifact.sessionId }],
        refetchType: 'all',
      });
      return;
    case 'note':
    case 'document':
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId: artifact.sessionId }],
      });
      // Notes and exported docs are document rows, so the tab strip reads them
      // from the document list too.
      void queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENTS_BY_ID, { sessionId: artifact.sessionId }],
        refetchType: 'all',
      });
      return;
    case 'tasks':
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId: artifact.sessionId }],
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_CLINICAL_TASKS_BY_SESSION_ID, { sessionId: artifact.sessionId }],
      });
      return;
    case 'codes':
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_CLINICAL_CODES, { sessionId: artifact.sessionId }],
      });
      return;
    case 'letter':
      void queryClient.invalidateQueries({
        queryKey: lettersQueryOptions(artifact.sessionId).queryKey,
      });
      return;
    case 'patient':
      void queryClient.invalidateQueries({ queryKey: [GET_PATIENT_PROFILES] });
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_DOCUMENTS, { patientProfileId: artifact.patientId }],
      });
      return;
    case 'created-objects':
      // Transcript-derived list — no backing query to invalidate. The rows
      // refresh their own object query when opened (each `target` artifact).
      return;
    case 'session-list':
      for (const sessionId of artifact.sessionIds) {
        void queryClient.invalidateQueries({
          queryKey: [GET_SESSION_BY_ID, { sessionId }],
        });
      }
      return;
    case 'calendar':
    case 'mail':
    case 'files':
    case 'insurance':
      // Snapshot artifacts — no backing react-query cache.
      return;
    case 'template-builder':
      // The template editor owns its own queries (template-creation-record +
      // template), refreshed by its own hooks on mount — nothing to invalidate
      // here. (Agent-driven emission of this artifact lands in Phase 2.)
      return;
    case 'form-template':
      // The PDF form editor owns its own template query on mount.
      return;
    case 'presentation':
      return;
    case 'memory':
      void queryClient.invalidateQueries({ queryKey: [AGENT_MEMORIES_QUERY_KEY] });
      return;
    case 'workflow':
      void queryClient.invalidateQueries({ queryKey: WORKFLOW_KEYS.all });
      // Exact schedule key so an already-mounted canvas toggle refreshes even
      // when a broad prefix invalidation races with a just-written schedule.
      void queryClient.invalidateQueries({
        queryKey: WORKFLOW_KEYS.schedule(artifact.workflowId),
        refetchType: 'all',
      });
      void queryClient.invalidateQueries({
        queryKey: WORKFLOW_KEYS.eventRules(artifact.workflowId),
        refetchType: 'all',
      });
      return;
    case 'document-export':
      // Ephemeral export preview — URL is already on the artifact; no cache.
      return;
    case 'run-attachment':
      // Presigned URL is minted by the panel; no react-query cache to refresh.
      return;
    case 'library-document':
      // Library doc preview — download URL is fetched by the panel; no cache.
      return;
    case 'library':
      // The panel owns a self-refreshing documents/jobs query.
      return;
    case 'note-push-verification':
      // Resolved store-first from the FE note-push candidate store (a jotai
      // atom the timeline ingest populates) — no react-query cache to refresh.
      return;
    case 'evidence-references':
      // Read out of the evidence citation store, which the answer's own
      // timeline publishes to — no react-query cache to refresh.
      return;
    default: {
      const _exhaustive: never = artifact;
      void _exhaustive;
    }
  }
};
